import React from 'react';
import {
  AgnesAutocomplete,
  AgnesCard,
  AgnesCardContent,
  AgnesCardHeader,
  AgnesCardTitle,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesLabel,
  AgnesPageHeader,
  AgnesProgress,
  AgnesStatCard,
} from '@agnes/ui';
import { FileText, FolderUp, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { ImportacaoStatus } from '@/components/importacao/ImportacaoStatus';
import { SearchInput } from '@/components/SearchInput';
import ServiceESocial from '@/services/ServiceESocial';
import { type RootCnpjItem, useCompaniesStore } from '@/stores/useCompaniesStore';
import { notify } from '@/stores/useToast';
import { confirm } from '@/stores/useConfirm';
import { mensagemErro } from '@/utils/apiError';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';
import { employerListMessage } from '@/utils/employerListMessage';
import { EmployerListRetryButton } from '@/components/EmployerListRetryButton';

const MAX_FILES = 50;
const MAX_SIZE = 5 * 1024 ** 3; // 5GB
const POLL_MS = Number(import.meta.env.VITE_APP_INTERVALO_REQUISICOES_MS || 300000);

// Situações que liberam a exclusão (CON-5268 + CON-4721): erro E falha — o guard
// server-side usa o mesmo conjunto (não-sucesso, não-processando).
const REMOVIVEIS = new Set(['Erro', 'Falha']);

// Cards totalizadores (CON-4721): chave canônica (= param `status` do backend) -> rótulo/cor.
// `key` vazia = card "Total" (limpa o filtro). A ordem é a dos mockups da Cristina.
type StatusCanon = '' | 'sucesso' | 'erro' | 'falha' | 'processando';
const CARDS: {
  key: StatusCanon;
  field: 'total' | 'sucesso' | 'erro' | 'falha' | 'processando';
  label: string;
  color: 'primary' | 'success' | 'error' | 'warning' | 'info';
}[] = [
  { key: '', field: 'total', label: 'Total', color: 'primary' },
  { key: 'sucesso', field: 'sucesso', label: 'Concluído com sucesso', color: 'success' },
  { key: 'erro', field: 'erro', label: 'Concluído com erro', color: 'error' },
  { key: 'falha', field: 'falha', label: 'Concluído com falha', color: 'warning' },
  { key: 'processando', field: 'processando', label: 'Em processamento', color: 'info' },
];

const ringAtivo: Record<string, string> = {
  primary: 'ring-agnes-primary',
  success: 'ring-agnes-success',
  error: 'ring-agnes-error',
  warning: 'ring-agnes-warning',
  info: 'ring-agnes-info',
};

interface Totais {
  total: number;
  sucesso: number;
  erro: number;
  falha: number;
  processando: number;
}
const TOTAIS_ZERO: Totais = { total: 0, sucesso: 0, erro: 0, falha: 0, processando: 0 };

type Importacao = Record<string, any>;

interface UploadItem {
  name: string;
  progress: number;
  status: 'enviando' | 'ok' | 'erro';
  mensagem?: string;
}

const dash = () => <span className="text-agnes-text-muted">-</span>;

function fmtData(iso: unknown): string {
  if (!iso) return '-';
  const d = new Date(iso as string);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('pt-BR');
}

/** Valida extensão .zip (case-insensitive) e tamanho ≤ 5GB. */
function isValidZip(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip') && file.size <= MAX_SIZE;
}

export default function PageImportacao() {
  // `busca` é o valor exibido no input (SearchInput controla o debounce);
  // `termo` é o valor efetivamente aplicado na busca (só muda quando o
  // SearchInput dispara onSearch, já debounced) — evita refetch a cada tecla.
  const [busca, setBusca] = React.useState('');
  const [termo, setTermo] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const [total, setTotal] = React.useState(0);
  // CON-5188: ordenação server-side. sortBy é a key da coluna (= alias aceito pelo backend).
  const [sortBy, setSortBy] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
  const [rows, setRows] = React.useState<Importacao[]>([]);
  const [uploads, setUploads] = React.useState<UploadItem[]>([]);
  // CON-4721: filtro por status (clique nos cards) + filtro de empregador (raiz CNPJ) +
  // contagens dos cards. `nrInsc` = raiz do empregador selecionado (undefined = todos).
  const [statusFiltro, setStatusFiltro] = React.useState<StatusCanon>('');
  const [empregador, setEmpregador] = React.useState<RootCnpjItem | null>(null);
  const [totais, setTotais] = React.useState<Totais>(TOTAIS_ZERO);
  const rootCnpjs = useCompaniesStore((s) => s.rootCnpjs);
  const carregandoEmpresas = useCompaniesStore((s) => s.loading);
  const empresasCarregadas = useCompaniesStore((s) => s.loaded);
  const erroEmpresas = useCompaniesStore((s) => s.erro);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const nrInsc = empregador?.value || undefined;

  const carregarLista = React.useCallback(
    async (
      p: number,
      size: number,
      q: string,
      sort?: string,
      status?: StatusCanon,
      insc?: string,
      silencioso = false,
    ) => {
      try {
        const { data } = await ServiceESocial.eSocialUpload.getAll(
          p,
          size,
          q || undefined,
          sort,
          status || undefined,
          insc,
        );
        setRows(Array.isArray(data?.content) ? data.content : []);
        setTotal(typeof data?.totalElements === 'number' ? data.totalElements : 0);
      } catch {
        if (!silencioso) notify.error('Erro ao buscar importações.');
      }
    },
    [],
  );

  // Contagens dos cards — mesmo escopo/busca/empregador da listagem, sem o filtro de
  // status (cada card mostra o seu próprio total). Silencioso: card não some se falhar.
  const carregarTotais = React.useCallback(async (q: string, insc?: string) => {
    try {
      const { data } = await ServiceESocial.eSocialUpload.getTotalizadores(q || undefined, insc);
      setTotais({ ...TOTAIS_ZERO, ...(data || {}) });
    } catch {
      setTotais(TOTAIS_ZERO);
    }
  }, []);

  // empresas do filtro de empregador (mesma fonte do resto do app).
  React.useEffect(() => {
    useCompaniesStore.getState().fetchRootCnpjs();
  }, []);

  React.useEffect(() => {
    const sort = sortBy ? `${sortBy},${sortOrder}` : undefined;
    void carregarLista(page, pageSize, termo, sort, statusFiltro, nrInsc);
    // processamento de zips grandes pode levar minutos — o polling atualiza a
    // grade sozinha conforme o import avança, sem precisar de F5.
    const t = setInterval(
      () => void carregarLista(page, pageSize, termo, sort, statusFiltro, nrInsc, true),
      POLL_MS,
    );
    return () => clearInterval(t);
  }, [page, pageSize, termo, sortBy, sortOrder, statusFiltro, nrInsc, carregarLista]);

  // Totais independem de página/ordenação/status — só de busca e empregador.
  React.useEffect(() => {
    void carregarTotais(termo, nrInsc);
  }, [termo, nrInsc, carregarTotais]);

  const atualizarProgresso = React.useCallback((name: string, progress: number) => {
    setUploads((prev) => prev.map((u) => (u.name === name ? { ...u, progress } : u)));
  }, []);

  const marcarStatus = React.useCallback(
    (name: string, status: UploadItem['status'], mensagem?: string) => {
      setUploads((prev) => prev.map((u) => (u.name === name ? { ...u, status, mensagem } : u)));
    },
    [],
  );

  const enviarArquivos = React.useCallback(
    async (fileList: FileList | File[]) => {
      const arquivos = Array.from(fileList).slice(0, MAX_FILES);
      const validos: File[] = [];
      for (const file of arquivos) {
        if (isValidZip(file)) {
          validos.push(file);
        } else {
          notify.error(`Arquivo inválido: ${file.name}. Aceito apenas .zip até 5GB.`);
        }
      }
      if (validos.length === 0) return;

      setUploads((prev) => {
        const restantes = prev.filter((u) => !validos.some((f) => f.name === u.name));
        return [
          ...restantes,
          ...validos.map((f) => ({ name: f.name, progress: 0, status: 'enviando' as const })),
        ];
      });

      let sucessos = 0;
      for (const file of validos) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await ServiceESocial.eSocialUpload.upload(fd, (e: ProgressEvent) => {
            atualizarProgresso(file.name, Math.round((e.loaded / (e.total || 1)) * 100));
          });
          atualizarProgresso(file.name, 100);
          marcarStatus(file.name, 'ok');
          sucessos += 1;
        } catch (err) {
          const msg = mensagemErro(err, `Erro ao enviar ${file.name}.`);
          marcarStatus(file.name, 'erro', msg);
          notify.error(msg);
        } finally {
          // Atualiza a listagem a cada arquivo, sem esperar o lote inteiro terminar.
          await carregarLista(
            page,
            pageSize,
            termo,
            sortBy ? `${sortBy},${sortOrder}` : undefined,
            statusFiltro,
            nrInsc,
          );
          void carregarTotais(termo, nrInsc);
        }
      }

      // Só comemora se algo realmente foi enviado — evita o "Importação enviada."
      // aparecer junto do toast de erro quando todos os arquivos do lote falham.
      if (sucessos > 0) notify.success('Importação enviada.');
    },
    [
      atualizarProgresso,
      marcarStatus,
      carregarLista,
      carregarTotais,
      page,
      pageSize,
      termo,
      sortBy,
      sortOrder,
      statusFiltro,
      nrInsc,
    ],
  );

  const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) {
      void enviarArquivos(e.target.files);
    }
    // permite reselecionar o mesmo arquivo
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      void enviarArquivos(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // CON-5268: excluir uma importação COM FALHA (libera nova tentativa do mesmo zip).
  const onExcluir = React.useCallback(
    async (r: Importacao) => {
      const ok = await confirm({
        title: 'Excluir importação',
        message: `Excluir a importação <b>${r.fileNameZip ?? ''}</b>? Isso libera uma nova tentativa de importação.`,
        okLabel: 'Excluir',
        cancelLabel: 'Cancelar',
      });
      if (!ok) return;
      try {
        await ServiceESocial.eSocialUpload.delete(r.id);
        notify.success('Importação excluída.');
        await carregarLista(
          page,
          pageSize,
          termo,
          sortBy ? `${sortBy},${sortOrder}` : undefined,
          statusFiltro,
          nrInsc,
        );
        void carregarTotais(termo, nrInsc);
      } catch (err) {
        notify.error(mensagemErro(err, 'Erro ao excluir a importação.'));
      }
    },
    [carregarLista, carregarTotais, page, pageSize, termo, sortBy, sortOrder, statusFiltro, nrInsc],
  );

  const columns: AgnesDataTableColumn<Importacao>[] = [
    {
      key: 'nome',
      header: 'Nome do arquivo',
      width: 280,
      sortable: true,
      render: (r) => r.fileNameZip ?? dash(),
    },
    {
      key: 'qtdXmls',
      header: 'Qtd. de XMLs',
      width: 160,
      sortable: true,
      render: (r) => r.amount ?? dash(),
    },
    {
      key: 'data',
      header: 'Data',
      width: 180,
      sortable: true,
      render: (r) => fmtData(r.uploadDateTime),
    },
    {
      key: 'usuario',
      header: 'Usuário',
      width: 200,
      sortable: true,
      render: (r) => r.userUploadXmls ?? dash(),
    },
    {
      key: 'situacao',
      header: 'Situação do processo',
      width: 300,
      sortable: true,
      render: (r) => <ImportacaoStatus situacao={r.situacao} />,
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center',
      render: (r) => (
        <AgnesIconButton
          aria-label="Excluir importação"
          data-qa={`importacao-acao-excluir-${r.id}`}
          size="sm"
          disabled={!REMOVIVEIS.has(r.statusProcess)}
          onClick={() => onExcluir(r)}
        >
          <Trash2 className="h-4 w-4" />
        </AgnesIconButton>
      ),
    },
  ];

  const onSortChange = (by: string, order: 'asc' | 'desc') => {
    setSortBy(by);
    setSortOrder(order);
    setPage(0);
  };

  // Clique no card: "Total" limpa o filtro; um card já ativo alterna de volta pra Total.
  const onCard = (key: StatusCanon) => {
    setStatusFiltro((prev) => (key === '' || prev === key ? '' : key));
    setPage(0);
  };

  // Filtro de empregador (seleção única): clicar no já selecionado limpa.
  const onEmpregador = (opt: RootCnpjItem) => {
    setEmpregador((prev) => (prev?.value === opt.value ? null : opt));
    setPage(0);
  };

  return (
    <>
      <AgnesPageHeader
        icon={<FileText />}
        title="Importação"
        subtitle="Importe informações a partir dos arquivos .xml do eSocial"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Configurações' },
          { label: 'eSocial' },
          { label: 'Importação' },
        ]}
      />

      <AgnesCard>
        <AgnesCardHeader>
          <AgnesCardTitle>Envio de arquivos</AgnesCardTitle>
        </AgnesCardHeader>

        <AgnesCardContent className="flex items-center">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-agnes-border px-4 py-12 text-center transition-colors hover:border-agnes-primary"
          >
            <FolderUp className="h-8 w-8 text-agnes-primary" />
            <p className="text-agnes-text">
              <b>Selecione até 50 arquivos para envio simultâneos</b> ou arraste e solte eles aqui
            </p>
            <p className="text-sm text-agnes-text-muted">Formato .zip (Máximo de 5Gb)</p>
            <input
              ref={inputRef}
              type="file"
              accept=".zip"
              multiple
              className="hidden"
              onChange={onFilesSelected}
            />
          </button>

          {uploads.length > 0 && (
            <ul className="relative mt-3 space-y-2">
              {uploads.map((u) => (
                <li key={u.name} className="text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-agnes-text">{u.name}</span>
                    <span
                      className={
                        u.status === 'erro'
                          ? 'text-agnes-error'
                          : u.status === 'ok'
                            ? 'text-agnes-success'
                            : 'text-agnes-text-muted'
                      }
                    >
                      {u.status === 'erro'
                        ? 'Erro'
                        : u.status === 'ok'
                          ? 'Enviado'
                          : `${u.progress}%`}
                    </span>
                  </div>
                  {u.status === 'enviando' && (
                    <AgnesProgress value={u.progress} size="sm" className="mt-1" />
                  )}
                  {u.status === 'erro' && u.mensagem && (
                    <p className="mt-1 text-xs text-agnes-error">{u.mensagem}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </AgnesCardContent>
      </AgnesCard>

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {CARDS.map((c) => {
          const ativo = statusFiltro === c.key;
          return (
            <button
              key={c.field}
              type="button"
              aria-pressed={ativo}
              data-qa={`importacao-card-${c.field}`}
              onClick={() => onCard(c.key)}
              className={`rounded-2xl text-left outline-none transition ${
                ativo ? `ring-2 ring-offset-2 ${ringAtivo[c.color]}` : 'hover:opacity-90'
              }`}
            >
              <AgnesStatCard title={c.label} value={totais[c.field]} color={c.color} />
            </button>
          );
        })}
      </div>

      <AgnesCard className="mt-4">
        <AgnesCardContent className="flex items-stretch">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-[240px] flex-1">
              <SearchInput
                value={busca}
                onValueChange={setBusca}
                onSearch={() => {
                  setPage(0);
                  setTermo(busca);
                }}
                placeholder="Pesquisar..."
              />
            </div>
            <div className="w-full sm:w-80">
              <AgnesLabel>Empregador</AgnesLabel>
              <AgnesAutocomplete<RootCnpjItem>
                data-qa="importacao-filtro-empregador"
                options={rootCnpjs}
                value={empregador ? [empregador] : []}
                onOptionToggle={onEmpregador}
                getOptionLabel={(o) => `${o.title} - ${o.subtitle}`}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                placeholder="Todos os empregadores"
                noOptionsText={employerListMessage({
                  loading: carregandoEmpresas,
                  loaded: empresasCarregadas,
                  error: erroEmpresas,
                })}
              />
              <EmployerListRetryButton
                error={erroEmpresas}
                onRetry={() => void useCompaniesStore.getState().fetchRootCnpjs()}
              />
            </div>
          </div>
          {rows.length === 0 ? (
            <EmptyState
              className="mt-4"
              icon={<FolderUp className="h-7 w-7" />}
              title="Nenhuma importação disponível"
            />
          ) : (
            <TableWrapper className="mt-4">
              <AgnesDataTable<Importacao>
                columns={columns}
                data={rows}
                rowKey={(r) => String(r.id ?? '')}
                sort={{ sortBy, sortOrder, onSortChange }}
                pagination={{
                  page,
                  pageSize,
                  total,
                  serverSide: true,
                  onPageChange: setPage,
                  onPageSizeChange: (s) => {
                    setPageSize(s);
                    setPage(0);
                  },
                }}
              />
            </TableWrapper>
          )}
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
